import React from "react";
import styled, { css } from "styled-components";
import { Loading03Icon } from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import { colors } from "src/theme/colors";
import { line, shadow } from "src/theme/ui";

/**
 * Button — the football web app's button, ported to this stack.
 *
 * Its design is flat: a solid accent fill with a soft brand-tinted drop, or a
 * white surface with a hairline that darkens on hover. No gradients, no inset
 * highlight. The heights come straight from that app's canvas — 34px for a
 * page action, 30px inside a card, 28px inside a table row, 27px inside a
 * list — and the corner is chosen by the height rather than by the colour,
 * except at 34px where the canvas splits an accent fill (10px) from a white
 * one (12px).
 *
 * Ported rather than imported: the original is Tailwind 4 classes on React 19,
 * and this app is styled-components on React 17.
 */

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "destructive"
  | "destructiveSolid"
  | "soft"
  | "ghost";

export type ButtonSize =
  | "xs"
  | "sm"
  | "md"
  | "default"
  | "lg"
  | "xl"
  | "iconXs"
  | "iconSm"
  | "icon";

const sizes: Record<ButtonSize, ReturnType<typeof css>> = {
  /* In-list action */
  xs: css`
    height: 27px;
    padding: 0 10px;
    gap: 6px;
    font-size: 11.5px;
  `,
  /* In-row action */
  sm: css`
    height: 28px;
    padding: 0 10px;
    font-size: 12px;
  `,
  /* In-card action */
  md: css`
    height: 30px;
    padding: 0 11px;
    font-size: 12.5px;
  `,
  /* Page action — the default */
  default: css`
    height: 34px;
    padding: 0 13px;
  `,
  lg: css`
    height: 36px;
    padding: 0 15px;
  `,
  /* The one screen where a button is the only thing on it */
  xl: css`
    height: 44px;
    padding: 0 16px;
    font-size: 14px;
  `,
  iconXs: css`
    width: 27px;
    height: 27px;
    padding: 0;
  `,
  iconSm: css`
    width: 30px;
    height: 30px;
    padding: 0;
  `,
  icon: css`
    width: 34px;
    height: 34px;
    padding: 0;
  `,
};

/* The corner comes from the height. */
const sizeRadius: Partial<Record<ButtonSize, number>> = {
  xs: 8,
  sm: 8,
  md: 8,
  lg: 9,
  xl: 11,
  iconXs: 8,
  iconSm: 8,
};

/* …except at 34px, where it is the fill's business, so a word and a glyph in
   the same toolbar still sit on the same corner as each other. */
const variantRadius: Record<ButtonVariant, number> = {
  primary: 10,
  secondary: 12,
  tertiary: 10,
  destructive: 12,
  destructiveSolid: 10,
  soft: 8,
  ghost: 10,
};

const variants: Record<ButtonVariant, ReturnType<typeof css>> = {
  primary: css`
    border-color: ${colors.primary[500]};
    background: ${colors.primary[500]};
    color: ${colors.white};
    font-weight: 560;
    box-shadow: 0 6px 14px -6px ${colors.primary[200]};

    &:hover,
    &:focus {
      border-color: ${colors.primary[600]};
      background: ${colors.primary[600]};
      color: ${colors.white};
    }
  `,
  /* The white page action */
  secondary: css`
    border-color: ${line.hairline};
    background: ${colors.white};
    color: ${colors.gray[800]};
    font-weight: 500;

    &:hover,
    &:focus {
      border-color: ${colors.gray[400]};
      background: ${colors.white};
      color: ${colors.gray[900]};
    }
  `,
  tertiary: css`
    color: ${colors.gray[700]};
    font-weight: 500;

    &:hover,
    &:focus {
      background: ${colors.gray[50]};
      color: ${colors.gray[900]};
    }
  `,
  /* A destructive action still reads as a quiet white button here; only the
     label carries the warning. */
  destructive: css`
    border-color: ${line.hairline};
    background: ${colors.white};
    color: ${colors.error[600]};
    font-weight: 500;

    &:hover,
    &:focus {
      border-color: ${colors.error[300]};
      background: ${colors.white};
      color: ${colors.error[700]};
    }
  `,
  /* Solid, for a genuine point of no return */
  destructiveSolid: css`
    border-color: ${colors.error[500]};
    background: ${colors.error[500]};
    color: ${colors.white};
    font-weight: 560;

    &:hover,
    &:focus {
      border-color: ${colors.error[600]};
      background: ${colors.error[600]};
      color: ${colors.white};
    }
  `,
  /* The tinted in-row call to action */
  soft: css`
    border-color: ${colors.primary[100]};
    background: ${colors.white};
    color: ${colors.primary[700]};
    font-weight: 520;

    &:hover,
    &:focus {
      border-color: ${colors.primary[200]};
      background: ${colors.primary[10]};
      color: ${colors.primary[700]};
    }
  `,
  ghost: css`
    color: ${colors.gray[700]};
    font-weight: 500;

    &:hover,
    &:focus {
      background: ${colors.gray[50]};
      color: ${colors.gray[900]};
    }
  `,
};

const Root = styled.button<{ $variant: ButtonVariant; $size: ButtonSize }>`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 1px solid transparent;
  font-size: 13px;
  font-family: inherit;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  outline: none;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  border-radius: ${({ $variant, $size }) =>
    sizeRadius[$size] ?? variantRadius[$variant]}px;

  ${({ $size }) => sizes[$size]}
  ${({ $variant }) => variants[$variant]}

  &:focus-visible {
    box-shadow: ${shadow.focusRing};
  }

  /* The press is a small squash rather than a colour step. */
  &:active {
    transform: scale(0.97);
  }

  /**
   * OFF, and readable while it is off.
   *
   * Every variant was drawn at half opacity, which on a filled button is
   * white type over a 50% wash of the accent: about 1.9:1 against the
   * ground, so the one control that most needs to be read — the primary
   * action, refused, with a reason beside it — was the least readable thing
   * on the dialog.
   *
   * A filled variant goes GREY instead: the fill drops out, the type takes
   * the muted ink, and the button reads as inert rather than as faint. The
   * outlined and quiet variants keep the fade, where it is only a border
   * and a word and the contrast survives it.
   */
  &:disabled {
    pointer-events: none;
  }

  &:disabled {
    ${({ $variant }) =>
      $variant === "primary" ||
      $variant === "destructiveSolid" ||
      $variant === "soft"
        ? css`
            border-color: ${colors.gray[200]};
            background: ${colors.gray[100]};
            color: ${colors.gray[400]};
            box-shadow: none;
          `
        : css`
            opacity: 0.5;
          `}
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:active {
      transform: none;
    }
  }

  svg {
    flex-shrink: 0;
    pointer-events: none;
  }
`;

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Swaps in a spinner and takes the button out of reach while it runs. */
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "default",
      type = "button",
      loading,
      disabled,
      children,
      ...props
    },
    ref,
  ) => (
    <Root
      ref={ref}
      type={type}
      $variant={variant}
      $size={size}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && <HugeIcon icon={Loading03Icon} size={13} spin />}
      {children}
    </Root>
  ),
);

Button.displayName = "Button";

export default Button;
